import React from "react";
import { useObserver } from "mobx-react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { store } from "@store";
import { BookingInvoiceType, InvoiceFragment } from "@graphql";
import { Body, Spinner, WithTooltip } from "@components";
import { makeStyles } from "@material-ui/core/styles";
import { InvoiceCard } from "./InvoiceCard";
import { openInNewWindow } from "@utils/openInNewWindow";
import { Form, Text, ToggleButtonGroup } from "@components/formx";
import { InvoiceDialogFormData } from "./utils";
import { FormStore } from "@modules/formx";
import { InvoiceMonth, InvoiceSplitType } from "@bonliva-crm/shared";

type Props = {
  form: FormStore<InvoiceDialogFormData>;
  canSubmit: boolean;
  loading: boolean;
  currentInvoice: InvoiceFragment;
  isInvoiceFound: boolean;
  onClose: () => void;
  withCustomNumbers: boolean;
  submitCustom: (v: string) => void;
};

export function BookingInvoiceDialog(props: Props) {
  const { form, loading, currentInvoice, isInvoiceFound } = props;

  const submitCustomInvoice = (code: string) => {
    const entry = Object.entries(store().cache.specialInvoices).find(
      (x) => x[1] === code
    );

    if (!entry) {
      return;
    }

    props.submitCustom(entry[0]);
  };

  const classes = useStyles({});

  return useObserver(() => (
    <Dialog open={true} onClose={props.onClose} classes={{ paper: classes.root }}>
      <DialogTitle>{locale.AddInvoiceNumber}</DialogTitle>

      <DialogContent>
        <Form store={form} allowUpdates>
          {loading && (
            <div className={classes.loading}>
              <Spinner size={16} />
            </div>
          )}
          <div className={classes.formRow}>
            <ToggleButtonGroup
              name="month"
              wrap
              label={locale.Month}
              options={Object.values(InvoiceMonth).map((v) => ({
                label: locale.invoiceMonth[v],
                value: v
              }))}
            />
          </div>
          <div className={classes.formRow}>
            <ToggleButtonGroup
              name="type"
              wrap
              label={locale.Type}
              options={Object.values(InvoiceSplitType).map((v) => ({
                label: locale.invoiceSplitType[v],
                value: v
              }))}
            />
          </div>

          <div className={classes.formRow}>
            <Text
              name={"invoiceNumber"}
              label={locale.InvoiceNumber}
              TextFieldProps={{
                autoFocus: true,
                inputProps: {
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      form.submit(e);
                    }
                  }
                }
              }}
              onChange={(e) => {
                if (typeof e.target.value === "string") {
                  e.target.value = e.target.value.replace(/[^0-9]/g, "");
                }
              }}
            />
          </div>

          {currentInvoice && (
            <WithTooltip title={locale.ClickForDetails}>
              <div className={classes.success} onClick={form.submit}>
                <InvoiceCard
                  data={currentInvoice}
                  onOpen={() => openInNewWindow(currentInvoice.PreviewUrl)}
                />
              </div>
            </WithTooltip>
          )}

          <div>
            {!isInvoiceFound && (
              <Body className={classes.error} color="error">
                {locale.NothingFound}
              </Body>
            )}
          </div>

          {props.withCustomNumbers && (
            <div className={classes.customBtns}>
              {Object.values(BookingInvoiceType).map((type) => (
                <Button
                  key={type}
                  size="small"
                  variant="outlined"
                  onClick={() => submitCustomInvoice(type)}
                >
                  {locale.bookingInvoiceType[type]}
                </Button>
              ))}
            </div>
          )}
        </Form>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={props.onClose}>
          {locale.Cancel}
        </Button>
      </DialogActions>
    </Dialog>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    width: "34rem"
  },

  loading: {
    padding: 8
  },

  success: {
    cursor: "pointer",
    padding: 8,
    border: `1px solid ${theme.palette.grey[400]}`,
    marginTop: 16,
    borderRadius: 4,
    transition: theme.transitions.create("background-color"),

    "&:hover": {
      backgroundColor: theme.palette.grey[200]
    }
  },

  error: {
    marginTop: 16,
    textAlign: "center"
  },

  customBtns: {
    ...theme.mixins.childrenMarginRight(4),
    display: "flex",
    flexWrap: "wrap",
    // justifyContent: "",
    marginTop: 16
  },

  formRow: {
    marginBottom: 8
  }
}));
